import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import errorLogger, { BUGSNAG_STATUSES } from '@picsart/growth-rc/services/errorLogger';
import { getContext } from 'configs/context';

import { fetchSubscriptionTiers, fetchUser, fetchUserTier } from 'services/userData';

import get from 'lodash/get';
import generalSlice from 'store/general';
import type { IUserData, User } from '../types';

const useUserData = () => {
  const dispatch = useDispatch();
  const userRef = useRef<User>();
  const context = getContext();

  const hashUserId = async (userId: any) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(userId);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  const setUserToDatadog = useCallback(async () => {
    const { datadogRum } = await import('@datadog/browser-rum');
    const authState = context.general.auth.getState();
    const user = authState?.id && (await fetchUser());

    const userId = await hashUserId(user.id);

    errorLogger.setUser({ id: user.id });
    datadogRum.setUser({ id: userId });
  }, [context]);

  const fetchUserAllData = async (authState: User): Promise<Partial<IUserData>> => {
    const [userTier, user, subscriptionTiers] = await Promise.all([fetchUserTier(), authState?.id && fetchUser(), fetchSubscriptionTiers()]);
    const isSubscribed = user?.subscription?.granted || getContext().monetization.userSubscription.getState()?.isSubscribed || false;
    const isTrialUsed = Boolean(user?.subscription?.is_trial_used);
    const isAuthorized = Boolean(authState?.id);
    const subscriptionType = get(userTier, 'response.id');
    const tiers = subscriptionTiers?.response || [];

    return {
      subscriptionType,
      isSubscribed,
      isAuthorized,
      isTrialUsed,
      tiers,
    };
  };

  useEffect(() => {
    const { onOpen } = context.handlers;
    onOpen(async () => {
      const { datadogRum } = await import('@datadog/browser-rum');

      const authState = context.general.auth.getState();
      const userData = await fetchUserAllData(authState);

      dispatch(
        generalSlice.actions.setUserData({
          ...userData,
          waitingUserSubscription: false,
        }),
      );

      if (authState?.id) {
        await setUserToDatadog().catch(error => console.error('setUserToDatadog failed:', error));
      }

      const unsubscribe = context.general.auth.subscribe(
        async nextValue => {
          const isSubscribed = getContext().monetization.userSubscription.getState()?.isSubscribed;
          if (nextValue?.id) {
            const sameUser = nextValue.id && nextValue.id === userRef.current?.id;
            const sameSubscription = isSubscribed === userRef.current?.isSubscribed;
            if (sameUser && sameSubscription) return;
            userRef.current = nextValue;

            const userInfo = await fetchUserAllData(nextValue);

            dispatch(
              generalSlice.actions.setUserData({
                ...userInfo,
                waitingUserSubscription: false,
              }),
            );
          } else {
            dispatch(
              generalSlice.actions.setUserData({
                isAuthorized: Boolean(nextValue?.id),
                isSubscribed: Boolean(isSubscribed),
                waitingUserSubscription: false,
              }),
            );
          }
        },
        { needPrevious: true },
      );

      return () => {
        unsubscribe();
        datadogRum.clearUser();
      };
    });
  }, [dispatch, setUserToDatadog, context]);

  useEffect(() => {
    async function getUserTier() {
      try {
        const userTier = await fetchUserTier();
        const userSubscriptionType = get(userTier, 'response.id');

        dispatch(
          generalSlice.actions.setUserData({
            subscriptionType: userSubscriptionType,
          }),
        );
      } catch (error) {
        errorLogger.trigger(error as Error, BUGSNAG_STATUSES.ERROR);
      }
    }

    // Subscribe to event when payment is successful
    const unsubscribe = context.handlers.subscribeToEvent(async event => {
      // PAYMENT_DONE is for web and status: success if for android
      if (event.type === 'PAYMENT_DONE' || event.status === 'success') {
        await getUserTier();
      }
    });

    return () => unsubscribe();
  }, [dispatch, context]);
};

export default useUserData;
